import {
  ISW,
  StepBooleanMetaPropNames,
  StepStringMetaPropNames,
  IImageComponent,
  IComment,
  IStepComment,
  ISWRevisionHistory,
  ITIMEImageComponent,
  IGetTimeMediaFileMapping,
  ImageType,
  ServiceLevel,
  Usage,
  ISiteLocation,
  IConnectedSWIsUploadError,
  INotice,
  IStepNotice,
  StepComponentType,
  IReferenceDoc,
  ITranslatedSWI
} from "interfaces/sw/SWInterfaces";
import { IOperation } from "interfaces/operations/Operations"
import { Item } from "./../../components/sw/manage/steps/Nestable";
import { IConfidentialSWUser } from "interfaces/user/UserInterfaces";
import { IMasterDataItem } from "interfaces/masterData/masterDataInterfaces";
import { IFetchRCStepsPayload } from "./manageSWActions";

export interface IManageSWState {
  newStepTempId?: number,
  containerRcId?: number,
  SW: ISW,
  ReusableCompFromTLMSWI: ISW,
  ReusableCompSortable: Item[],
  reusableCompSelectedType?: ReusableCompType,
  isCreateReusableContentOpen: boolean,
  isCreateRCNoticeOpen: boolean,
  isSearchRCNoticeOpen: boolean,
  rcNoticeStep: string,
  isDirty: number | false,
  currentTab: ManageSWTabs,
  loadOperation?: IOperation,
  createOperation?: IOperation,
  updateOperation?: IOperation,
  backgroundUpdateOperation?: IOperation,
  publishOperation?: IOperation,
  fileUploadOperation?: IOperation,
  stepVisibilities: IStepVisibility[],
  noticeVisibilities: INoticeVisibility[],
  scrollToStepGuid?: string,
  scrollToParentGuid?: string | null,
  scrollToStepId?: number,
  scrollToSortOrder?: number,
  commentData: ISWCommentData,
  swNoticeData: ISWNotices,
  swStepNoticeData: ISWStepNotices,
  stepCommentData: ISWStepCommentData,
  currentLocation: IExecutionPreviewLocation | null,
  rcPublishData: IRCPublishData,
  revisionHistories: ISWRevisionHistory[],
  translatedSWIs: ITranslatedSWI[]
  timeImageData: ITimeImageData,
  timeMediaFilesMappingData: IGetTimeMediaFilesMappingData,
  redirectToSearchPageFromTimeRC: boolean,
  submitForReviewFlag: boolean,
  confidentialSWData: IConfidentialSWData,
  siteLocations: ISiteLocation[],
  connectedSWIData: ISWConnectedSWIData,
  connectedSWIsUploadErrors: IConnectedSWIsUploadError[],
  enableMetersValue: boolean,
  enableFormulaComponentValue: boolean,
  disableMFGInputComponentValues: string[],
  disableMFGContentComponentValues: string[],
  mfgDisableVisionInspection: boolean,
  enableConfiguredMFGPlantsInTLMValue: boolean,
  pendingCompatibilityFetchRCStepsAction: IFetchRCStepsPayload | undefined,
  copiedComponents: StepComponentType[],
  enableConnectedSWI: boolean,
  enableFco: boolean,
  enableCrwo: boolean,
  enableMasterPm: boolean,
  tlmNonMandatoryEpicEquipmentWithEvent: string[],
  plantsForMFGJobByPlants: string[],
  mviEnabled: boolean,
  woSplitEnabled: boolean,
  maxRowCountForTableComponent: number,
}

export interface IConfidentialSWData {
  changeOperation: IOperation;
  loadOperation: IOperation;
  confidentialEnabledUsers: IConfidentialSWUser[];
}

export enum ReusableCompType {
  Task,
  Step,
  SubStep,
  Notice
}

export interface ISWCommentData {
  comments?: IComment[],
  loadOperation?: IOperation,
}

export interface IRCPublishData {
  dependentSWsCount: number,
  loadOperation?: IOperation,
}

export interface IMergeMenuItem {
  title: string,
  submenu?: IMergeMenuItem[],
}

export interface ISWStepCommentData {
  stepID?: number,
  rcContainerStepID?: number,
  comments?: IStepComment[],
  loadOperation?: IOperation,
}

export enum ManageSWTabs {
  Attributes,
  Steps,
  RefDocs,
  Comments,
  RevisionHistory,
  ConnectedSWIs,
  NoticeSummary,
  TranslatedSWIs,
}

export enum ManageSWMetadataTabs {
  Tlm,
  Psd,
  Mfg,
}

export interface IStepBooleanPayload {
  stepGuid: string,
  value: boolean,
  propName: StepBooleanMetaPropNames,
}

export interface IStepStringPayload {
  stepGuid: string,
  value: string,
  propName: StepStringMetaPropNames,
}

export interface IServiceLevelPayload {
  stepGuid: string,
  value: ServiceLevel,
  stepId?: number,
}
export interface ISiteLocationPayload {
  stepGuid: string,
  value: string,
}

export interface IUsagePayload {
  stepGuid: string,
  value: Usage,
}

export interface IStepSortOrderPayload {
  oldIndex: number,
  newIndex: number,
  parentStepGuid?: string,
}

export interface IRefDocSortOrderPayload {
  oldIndex: number,
  newIndex: number,
}

export interface ISWAttachment {
  swGuid: string,
  swVersion: number,
  file: File,
  type: SWAttachmentTypes,
  refDocGuid?: string,
  imageComponent?: IImageComponent,
  stepGuid?: string,
}
export interface IPasteSWComponentToSWDraft {
  sourceSWGuid: string,
  sourceSWVersion: number,
  filenames: string[],
  swGuid: string,
  stepGuid: string,
  component: StepComponentType
}

export interface ICopySWAttachmentsToSWDraft {
  sourceSWGuid: string,
  sourceSWVersion: number,
  fileNames: string[],
  destinationSWGuid: string
}

export interface ITimeImageAttachment {
  swGuid: string,
  swVersion: number,
  label: string,
  link: string,
  imageNumber: number,
  type: SWAttachmentTypes,
  timeImageComponent?: ITIMEImageComponent,
  stepGuid?: string,
}

export enum SWAttachmentTypes {
  RefDoc,
  ImageComponent,
  TIMEImageComponent,
}

export interface ITimeImageData {
  imageNumber: number,
  link: string,
  label: string,
  loadOperation?: IOperation,
}

export interface IGetTimeMediaFilesMappingData {
  timeMediaFileMapping: IGetTimeMediaFileMapping[],
  loadTimeImagesOperation?: IOperation
}

export interface IStepComponentImageRequest {
  swGuid: string,
  swVersion: number,
  filename: string,
  imageComponent: IImageComponent,
  stepGuid: string,
  destination: ImageDataDestinations,
  rcGuid?: string,
  rcVersion?: number,
  masterStepId?: number,
  rcContainerStepId?: number,
}

export interface ITableCellImagesRequest {
  stepGuid: string,
  componentGuid: string,
  rowIndex: number,
  colIndex: number,
  swGuid: string,
  swVersion: number,
  filename: string,
  destination: ImageDataDestinations,
  rcGuid?: string,
  rcVersion?: number,
  masterStepId?: number,
  rcContainerStepId?: number,
}

export interface IRefDocTableCellImagesRequest {
  refDocGuid: string,
  rowIndex: number,
  colIndex: number,
  swGuid: string,
  swVersion: number,
  filename: string,
  destination: ImageDataDestinations,
}

export interface IStepComponentTIMEImageRequest {
  swGuid: string,
  swVersion: number,
  filename: string,
  timeImageComponent: ITIMEImageComponent,
  stepGuid: string,
  destination: ImageDataDestinations,
  masterStepId?: number,
  rcContainerStepId?: number,
}

export enum ImageDataDestinations {
  ManageSW,
  Approval,
}

export interface IStepVisibility {
  stepGuid: string,
  isOpen: boolean,
  stepId?: number
}

export interface INoticeVisibility {
  noticeGuid: string,
  isOpen: boolean,
}

export interface INoticeImageRequest {
  swGuid: string,
  swVersion: number,
  noticeGuid: string,
  stepGuid?: string,
  filename: string,
  isDirty?: boolean,
  destination: ImageDataDestinations,
  imageType: ImageType,
  rcGuid?: string,
  rcVersion?: number,
}

export interface INoticeAttachment {
  swGuid: string,
  swVersion: number,
  file?: File,
  noticeGuid: string,
  stepGuid?: string,
}

export interface INoticeTimeImageAttachment {
  swGuid: string,
  swVersion: number,
  label: string,
  link: string,
  imageNumber: number,
  noticeGuid: string,
  stepGuid?: string,
}

export interface ITableTimeImageAttachment {
  swGuid: string,
  swVersion: number,
  label: string,
  link: string,
  imageNumber: number,
  componentGuid: string,
  stepGuid?: string,
  rowIndex: number,
  colIndex: number,
  value: string,
  altTagValue: string,
  timeImageTarget: AddTimeImageTargets
}

export enum AddTimeImageTargets {
  RefDocs = "RefDocs",
  ManageSW = "ManageSW"
}

export interface IVideoAttachment {
  swGuid: string,
  swVersion: number,
  file: File,
  stepGuid: string,
  componentGuid: string,
}

export interface IAudioAttachment {
  swGuid: string,
  swVersion: number,
  file: File,
  stepGuid: string,
  componentGuid: string,
}

export interface ITableComponentImage {
  swGuid: string,
  swVersion: number,
  imageObject: ITableImageFile,
  stepGuid?: string,
  componentGuid: string,
  rowIndex: number,
  colIndex: number,
  imageUpdate?: any
}

export interface IRefDocImage {
  swGuid: string,
  swVersion: number,
  imageObject: ITableImageFile,
  refDocGuid: string,
  rowIndex: number,
  colIndex: number,
}

export interface ITableImageFile {
  file: File,
  localName: string,
}

export interface IVideoRequest {
  swGuid: string,
  swVersion: number,
  stepGuid: string,
  componentGuid: string,
  filename: string,
  isDirty?: boolean,
  destination: ImageDataDestinations,
  rcGuid?: string,
  rcVersion?: number,
}

export interface IAudioRequest {
  swGuid: string,
  swVersion: number,
  stepGuid: string,
  componentGuid: string,
  filename: string,
  isDirty?: boolean,
  destination: ImageDataDestinations,
  rcGuid?: string,
  rcVersion?: number,
}

export interface IExecutionPreviewLocation {
  stepGuid: string,
}

export interface IOOSPayload {
  stepGuid: string,
  item?: IMasterDataItem,
  isAdding?: boolean
}

export interface IMaintenanceTaskPayload {
  stepGuid: string,
  item?: IMasterDataItem,
  isAdding?: boolean,
  stepId?: number
}

export interface ISWConnectedSWIData {
  addOperation?: IOperation,
  deleteOperation?: IOperation,
  draft: {
    material?: IMasterDataItem,
    group?: IMasterDataItem,
    groupCounter?: IMasterDataItem
  }
}
export interface ISWNotices {
  swNotices?: INotice[],
}
export interface ISWStepNotices {
  swStepNotices?: IStepNotice[]
}
export interface IPasteComponentDraftRefDoc {
  swGuid: string,
  refDoc: IReferenceDoc,
  sourceSWGuid: string,
  sourceSWVersion: number,
  filenames: string[]
}